import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Globe, MapPin, ClipboardList, Share2, Star, Link2, Lock, Package, Share, Check, ExternalLink, MessageSquarePlus } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const host = (u) => { try { return new URL(u).hostname.replace(/^www\./, ''); } catch { return u; } };

export function Stars({ value = 0, size = 'w-3.5 h-3.5' }) {
  return <span className="inline-flex gap-0.5" aria-label={`${value} out of 5`}>{[1, 2, 3, 4, 5].map(i => <Star key={i} className={`${size} ${i <= Math.round(value) ? 'text-brand-orange fill-brand-orange' : 'text-warm-border'}`} />)}</span>;
}

export function ShareProfileButton({ shareUrl, className = '' }) {
  const [ok, setOk] = useState(false);
  const share = async () => {
    try {
      if (navigator.share) { await navigator.share({ title: 'Adoptic business profile', url: shareUrl }); return; }
      await navigator.clipboard.writeText(shareUrl); setOk(true); toast.success('Profile link copied'); setTimeout(() => setOk(false), 1500);
    } catch { /* cancelled */ }
  };
  return (
    <button onClick={share} data-testid="share-profile-btn" className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-warm-border bg-white text-ink font-body text-sm font-medium hover:bg-bg-warm transition-colors ${className}`}>
      {ok ? <Check className="w-3.5 h-3.5" /> : <Share className="w-3.5 h-3.5" />} {ok ? 'Copied' : 'Share profile'}
    </button>
  );
}

// Link-in-bio style hub of every business link (seeded + owner-added)
export function LinksHub({ links, businessId, claimed }) {
  const items = [];
  if (links?.website) items.push({ icon: Globe, label: 'Website', sub: host(links.website), url: links.website, tid: 'link-website' });
  if (links?.google_business) items.push({ icon: MapPin, label: 'Google Business Profile', sub: links.google_rating ? `${links.google_rating} ★ · ${links.google_review_count || 0} Google reviews` : 'View on Google Maps', url: links.google_business, tid: 'link-google' });
  (links?.directories || []).forEach((u, i) => items.push({ icon: ClipboardList, label: 'Directory listing', sub: host(u), url: u, tid: `link-directory-${i}` }));
  (links?.socials || []).forEach((u, i) => items.push({ icon: Share2, label: 'Social media', sub: host(u), url: u, tid: `link-social-${i}` }));
  if (links?.review_url) items.push({ icon: Star, label: 'Leave a review', sub: host(links.review_url), url: links.review_url, tid: 'link-reviews' });
  (links?.custom || []).forEach((l, i) => items.push({ icon: Link2, label: l.label, sub: host(l.url), url: l.url, tid: `link-custom-${i}` }));
  return (
    <div className="solid-card p-5" data-testid="links-hub">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-lg font-extrabold text-ink">Online presence</h3>
        <span className="font-body text-xs text-mid">{items.length} link{items.length === 1 ? '' : 's'}</span>
      </div>
      {items.length === 0 ? (
        <p className="font-body text-sm text-mid">No links detected yet.{!claimed && <> <Link to={`/claim/${businessId}`} className="text-brand-orange font-semibold">Claim this business</Link> to add your website, Google profile, directories and social pages.</>}</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {items.map(it => (
            <a key={it.tid} href={it.url} target="_blank" rel="noreferrer noopener" data-testid={it.tid}
              className="flex items-center gap-3 p-3 rounded-[12px] border border-warm-border bg-white hover:border-brand-orange hover:-translate-y-[1px] transition-all group">
              <span className="w-9 h-9 rounded-[10px] bg-bg-warm text-ink-2 flex items-center justify-center group-hover:bg-accent-light group-hover:text-brand-orange transition-colors"><it.icon className="w-4 h-4" strokeWidth={1.75} /></span>
              <span className="flex-1 min-w-0"><span className="block font-body text-sm font-semibold text-ink truncate">{it.label}</span><span className="block font-body text-xs text-mid truncate">{it.sub}</span></span>
              <ExternalLink className="w-3.5 h-3.5 text-mid" />
            </a>
          ))}
        </div>
      )}
      {claimed && <p className="font-body text-[11px] text-mid mt-3">Every link the owner adds improves this business's rank in the Tees Valley register.</p>}
    </div>
  );
}

export function ProductsSection({ listings = [], claimed, businessId, isOwner }) {
  return (
    <div className="solid-card p-5" data-testid="products-section">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <h3 className="font-display text-lg font-extrabold text-ink flex items-center gap-2"><Package className="w-4 h-4 text-brand-orange" /> Products & Services</h3>
        {isOwner && <Link to="/my-business?tab=listings" className="font-body text-xs font-semibold text-brand-orange hover:underline" data-testid="products-manage">Manage listings →</Link>}
      </div>
      {!claimed ? (
        <div className="rounded-[14px] border border-dashed border-warm-border p-5 flex items-start gap-3 bg-bg-warm/50" data-testid="products-locked">
          <span className="w-9 h-9 rounded-[10px] bg-white border border-warm-border flex items-center justify-center text-mid"><Lock className="w-4 h-4" /></span>
          <div className="flex-1">
            <p className="font-body text-sm font-semibold text-ink">Products & services unlock when the owner claims this business</p>
            <p className="font-body text-xs text-mid mt-0.5">Listings appear here and boost the business's position in Adoptic search.</p>
            <Link to={`/claim/${businessId}`} className="inline-flex mt-3 px-4 py-1.5 rounded-full bg-brand-orange text-white font-body text-xs font-semibold hover:bg-highlight-orange" data-testid="products-claim-cta">I own this business — claim it →</Link>
          </div>
        </div>
      ) : listings.length === 0 ? (
        <p className="font-body text-sm text-mid" data-testid="products-empty">The owner hasn't listed any products or services yet.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="products-grid">
          {listings.map(l => (
            <div key={l.id} className="rounded-[14px] border border-warm-border p-4 bg-white" data-testid={`profile-listing-${l.id}`}>
              <span className="font-body text-[10px] uppercase tracking-wider text-mid font-semibold">{l.category}</span>
              <p className="font-body text-sm font-semibold text-ink mt-1">{l.name}</p>
              {l.description && <p className="font-body text-xs text-ink-2 mt-1">{l.description}</p>}
              <p className="font-body text-sm font-bold text-ink mt-2">{l.price_label || 'By quote'}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function ReviewsSection({ businessId, reviews = [], summary, isOwner, onAdded }) {
  const [form, setForm] = useState({ author_name: '', rating: 5, text: '', email: '' });
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await api.post(`/profile/${businessId}/reviews`, form);
      toast.success('Thanks — your review is live'); setForm({ author_name: '', rating: 5, text: '', email: '' }); setOpen(false); onAdded && onAdded(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not post review'); }
    finally { setBusy(false); }
  };
  return (
    <div className="solid-card p-5" data-testid="reviews-section">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div>
          <h3 className="font-display text-lg font-extrabold text-ink">Adoptic reviews</h3>
          <div className="flex items-center gap-2 mt-1">
            <Stars value={summary?.average || 0} />
            <span className="font-body text-sm font-semibold text-ink" data-testid="reviews-average">{summary?.average ?? '–'}</span>
            <span className="font-body text-xs text-mid" data-testid="reviews-count">({summary?.count || 0} review{summary?.count === 1 ? '' : 's'})</span>
          </div>
        </div>
        {!isOwner && <button onClick={() => setOpen(o => !o)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-ink text-white font-body text-sm font-semibold hover:opacity-90" data-testid="review-open-form"><MessageSquarePlus className="w-4 h-4" /> Leave a review</button>}
      </div>
      {open && !isOwner && (
        <form onSubmit={submit} className="rounded-[14px] border border-warm-border p-4 bg-bg-warm/40 space-y-3 mb-4" data-testid="review-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input required value={form.author_name} onChange={e => setForm({ ...form, author_name: e.target.value })} placeholder="Your name" data-testid="review-name" className="h-10 rounded-[10px] border border-warm-border bg-white px-3 font-body text-sm" />
            <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="Email (optional, never shown)" data-testid="review-email" className="h-10 rounded-[10px] border border-warm-border bg-white px-3 font-body text-sm" />
          </div>
          <div className="flex items-center gap-2" data-testid="review-rating">
            <span className="font-body text-xs text-ink-2">Your rating</span>
            {[1, 2, 3, 4, 5].map(i => <button type="button" key={i} onClick={() => setForm({ ...form, rating: i })} data-testid={`review-star-${i}`} aria-label={`${i} stars`}><Star className={`w-6 h-6 transition-colors ${i <= form.rating ? 'text-brand-orange fill-brand-orange' : 'text-warm-border hover:text-brand-orange/60'}`} /></button>)}
          </div>
          <textarea required minLength={10} rows={3} value={form.text} onChange={e => setForm({ ...form, text: e.target.value })} placeholder="What was your experience with this business?" data-testid="review-text" className="w-full rounded-[10px] border border-warm-border bg-white p-3 font-body text-sm" />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 rounded-full border border-warm-border font-body text-sm text-ink-2 hover:bg-bg-warm" data-testid="review-cancel">Cancel</button>
            <button type="submit" disabled={busy} className="px-4 py-2 rounded-full bg-brand-orange text-white font-body text-sm font-semibold hover:bg-highlight-orange disabled:opacity-60" data-testid="review-submit">{busy ? 'Posting…' : 'Post review'}</button>
          </div>
        </form>
      )}
      {reviews.length === 0 ? (
        <p className="font-body text-sm text-mid" data-testid="reviews-empty">No reviews yet — be the first to share your experience.</p>
      ) : (
        <div className="space-y-3" data-testid="reviews-list">
          {reviews.map(r => (
            <div key={r.id} className="rounded-[14px] border border-warm-border p-4 bg-white" data-testid={`review-${r.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2"><span className="w-7 h-7 rounded-full bg-bg-warm text-ink-2 font-body text-xs font-bold flex items-center justify-center">{(r.author_name || '?')[0].toUpperCase()}</span><span className="font-body text-sm font-semibold text-ink">{r.author_name}</span>{r.verified_user && <span className="font-body text-[10px] px-1.5 py-0.5 rounded-full bg-forest-light text-forest font-semibold">Signed-in</span>}</div>
                <div className="flex items-center gap-2"><Stars value={r.rating} /><span className="font-body text-[11px] text-mid">{new Date(r.created_at).toLocaleDateString()}</span></div>
              </div>
              <p className="font-body text-sm text-ink-2 mt-2 leading-relaxed">{r.text}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
